import React, { ReactNode, useCallback, useId } from 'react';
import { IconCustomAurora } from '@rossum/ui/icons/tabler';
import {
  alpha,
  Button,
  CircularProgress,
  inputClasses,
  outlinedInputClasses,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';

export type CopilotComposerProps = {
  value: string;
  setValue: (v: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  placeholder: string;
  onSubmit: (value: string) => void;
  isSuggesting: boolean;
  isError: boolean;
  errorText: string;
  confirmText: string;
  submitDisabled?: boolean;
  submitDisabledTooltipTitle?: string;
  renderCopilotToolbar?: ReactNode;
  autoFocus?: boolean;
};

const COPILOT_TOOLBAR_TOP = 12;
const COPILOT_TOOLBAR_RIGHT = 12;

export const CopilotComposer = ({
  value,
  setValue,
  inputRef,
  placeholder,
  onSubmit,
  isSuggesting,
  isError,
  errorText,
  confirmText,
  submitDisabled,
  submitDisabledTooltipTitle,
  renderCopilotToolbar,
  autoFocus,
}: CopilotComposerProps) => {
  const intl = useIntl();
  const t = useTheme();
  const errorTextId = useId();

  const auroraLightColor = t.palette.aurora.light;
  const auroraMainColor = t.palette.aurora.main;
  const isDarkMode = t.palette.mode === 'dark';

  const submitButtonWrapperStyle = {
    position: 'absolute' as const,
    bottom: '12px',
    right: '12px',
  };

  const shimmerGradient = isDarkMode
    ? `linear-gradient(
        270deg,
        ${alpha(auroraMainColor, 0.8)} 0%,
        ${alpha(auroraLightColor, 0.8)} 50%,
        ${alpha(auroraMainColor, 0.8)} 100%
      )`
    : `linear-gradient(
        270deg,
        ${alpha(auroraMainColor, 0.8)} 0%,
        ${alpha(auroraLightColor, 0.6)} 50%,
        ${alpha(auroraMainColor, 0.8)} 100%
      )`;

  const textShimmerGradient = isDarkMode
    ? `linear-gradient(
        270deg,
        ${t.palette.grey[600]} 0%,
        ${t.palette.grey[300]} 50%,
        ${t.palette.grey[600]} 100%
      )`
    : `linear-gradient(
        270deg,
        ${t.palette.grey[300]} 0%,
        ${t.palette.grey[600]} 50%,
        ${t.palette.grey[300]} 100%
      )`;

  const handleKeyDownInMultilineInput = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !submitDisabled) {
        event.preventDefault();
        onSubmit(value);
      }
    },
    [onSubmit, submitDisabled, value]
  );

  // No zIndex on the root on purpose: inline consumers live inside a PageLayout whose
  // sticky header is itself zIndex 2, and the composer would paint over it. Stacking is
  // the job of whoever overlaps the composer with something — see CopilotDialogV2.
  return (
    <Stack>
      <Stack position="relative">
        <TextField
          onChange={e => setValue(e.currentTarget.value)}
          value={value}
          size="small"
          autoComplete="off"
          inputRef={inputRef}
          disabled={isSuggesting}
          multiline
          maxRows={12}
          minRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: `  ${t.palette.background.paper} !important`,
            },
          }}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onKeyDown={handleKeyDownInMultilineInput}
          InputProps={{
            sx: {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isError ? t.palette.warning.main : undefined,
              },
              [`&.${inputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: auroraLightColor,
                },
              pt: renderCopilotToolbar ? 6 : 2,
              pb: 6.5,
              px: 1.5,
              ...(isSuggesting && {
                '& textarea': {
                  backgroundImage: `${textShimmerGradient} !important`,
                  backgroundColor: 'transparent !important',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text !important',
                  backgroundClip: 'text !important',
                  WebkitTextFillColor: 'transparent !important',
                  color: 'transparent !important',
                  animation: 'shimmer 2000ms linear infinite',
                  opacity: '1 !important',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% center' },
                    '100%': { backgroundPosition: '-200% center' },
                  },
                },
              }),
            },
            inputProps: {
              'data-cy': 'prompt-field',
              'aria-describedby': isError ? errorTextId : undefined,
            },
          }}
        />

        <Tooltip
          title={
            submitDisabled && !isSuggesting
              ? (submitDisabledTooltipTitle ?? '')
              : ''
          }
          placement="top"
        >
          {/* This is for disabled button to trigger showing of the Tooltip */}
          <span style={submitButtonWrapperStyle}>
            <Button
              type="button"
              onClick={() => onSubmit(value)}
              variant="contained"
              color="aurora"
              disabled={submitDisabled}
              data-cy="submit-copilot-dialog-btn"
              startIcon={
                isSuggesting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SvgIcon fontSize="medium">
                    <IconCustomAurora />
                  </SvgIcon>
                )
              }
              sx={{
                ...(isSuggesting && {
                  '@keyframes shimmer': {
                    '0%': {
                      backgroundPosition: '200% center',
                    },
                    '100%': {
                      backgroundPosition: '-200% center',
                    },
                  },
                  pointerEvents: isSuggesting ? 'none' : undefined,
                  background: shimmerGradient,
                  backgroundSize: '200% auto',
                  animation: 'shimmer 2000ms ease-in-out infinite',
                }),
              }}
            >
              <span>
                {isSuggesting
                  ? intl.formatMessage({
                      id: 'features.rules.copilotProcessing',
                    })
                  : confirmText}
              </span>
            </Button>
          </span>
        </Tooltip>
        {renderCopilotToolbar ? (
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              position: 'absolute',
              top: `${COPILOT_TOOLBAR_TOP}px`,
              right: `${COPILOT_TOOLBAR_RIGHT}px`,
              width: `calc(100% - (${COPILOT_TOOLBAR_RIGHT}px + ${COPILOT_TOOLBAR_TOP}px))`,
            }}
          >
            {renderCopilotToolbar}
          </Stack>
        ) : null}
      </Stack>

      {isError ? (
        <Typography
          id={errorTextId}
          role="status"
          aria-live="polite"
          variant="caption"
          sx={{
            mt: 1,
            color: t.palette.warning.main,
            whiteSpace: 'pre-wrap',
            // The agent answers in its own words, which can run long — keep it readable
            // without letting it push everything below out of the view.
            maxHeight: '9em',
            overflowY: 'auto',
          }}
        >
          {errorText}
        </Typography>
      ) : null}
    </Stack>
  );
};
